import * as React from "react";
import { CalendarDays } from "lucide-react";

import { cn } from "../lib/cn";
import { Calendar } from "./calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

/**
 * DateTimePicker — the single date / datetime control for the whole app.
 *
 * Popover-anchored :class:`Calendar` (shadcn over react-day-picker v9,
 * Italian Monday-anchored) + an optional time row + "Cancella". Extracted
 * (June 2026) from the inline picker that lived in
 * ``ando/ActionFieldEditor::DateRenderer`` so EVERY surface that needs a
 * date — HITL action forms (``create_event``, due dates), the email
 * "Programma invio" composer, the Lavoro task composer — renders the SAME
 * control instead of the native ``<input type="datetime-local">`` (which
 * ignores the shell typography, opens OS chrome with Sunday-anchored /
 * ISO-week layout, and has no "Oggi" shortcut). DRY: one component, one look.
 *
 * Pure ``Date | null`` in / out — callers own wire-format conversion
 * (``"YYYY-MM-DD"`` / ``"YYYY-MM-DDTHH:mm"`` / ISO-UTC), so the picker stays
 * format-agnostic and reusable.
 */
export interface DateTimePickerProps {
  /** Currently-selected instant, or ``null`` when empty. */
  readonly value: Date | null;
  /** Emitted on every pick / time change / clear. */
  readonly onChange: (next: Date | null) => void;
  /** Show the time row (datetime) vs date-only. Default ``false``. */
  readonly withTime?: boolean;
  readonly disabled?: boolean;
  readonly readOnly?: boolean;
  readonly invalid?: boolean;
  /** Trigger placeholder when empty. */
  readonly placeholder?: string;
  /** id forwarded to the trigger button (label ``htmlFor`` association). */
  readonly id?: string;
  /** Override / extend the trigger button classes. */
  readonly triggerClassName?: string;
  /** Disable days strictly before this date (e.g. future-only scheduling). */
  readonly minDate?: Date;
  /** ``"HH:mm"`` applied when a day is picked and no time exists yet. */
  readonly defaultTime?: string;
  /** Minutes between the selectable times. Default ``30``. */
  readonly timeStep?: number;
  /** Popover alignment. Default ``"start"``. */
  readonly align?: "start" | "center" | "end";
  /** Show the "Cancella" footer. Default ``true``. */
  readonly clearable?: boolean;
  readonly ariaLabel?: string;
  /**
   * Render the Calendar (+ time) INLINE — no trigger button, no popover.
   * Use inside a surface that is already a popover/menu (e.g. the email
   * "Programma invio" panel) to avoid nesting popovers.
   */
  readonly inline?: boolean;
}

const DATE_FMT = new Intl.DateTimeFormat("it-IT", {
  weekday: "short",
  day: "numeric",
  month: "short",
  year: "numeric",
});
const TIME_FMT = new Intl.DateTimeFormat("it-IT", {
  hour: "2-digit",
  minute: "2-digit",
});

function triggerLabel(d: Date, withTime: boolean): string {
  const date = DATE_FMT.format(d);
  return withTime ? `${date} · ${TIME_FMT.format(d)}` : date;
}

/** Strip to local midnight (date-only comparisons / minDate). */
function startOfDay(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  return out;
}

const _pad2 = (n: number) => String(n).padStart(2, "0");

function buildTimeSlots(stepMinutes: number): string[] {
  const step = stepMinutes > 0 && stepMinutes <= 720 ? stepMinutes : 30;
  const slots: string[] = [];
  for (let mins = 0; mins < 24 * 60; mins += step) {
    slots.push(`${_pad2(Math.floor(mins / 60))}:${_pad2(mins % 60)}`);
  }
  return slots;
}

/**
 * Parse a wire ``"YYYY-MM-DD"`` (local midnight) to a Date, or ``null``.
 * Shared bridge so every date-only consumer (FiC payment, finance snapshot,
 * …) speaks the same format with :class:`DateTimePicker` (Date in/out).
 */
export function ymdToDate(s: string | null | undefined): Date | null {
  if (!s) return null;
  const d = new Date(`${s}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Format a Date back to local ``"YYYY-MM-DD"``. */
export function dateToYmd(d: Date): string {
  return `${d.getFullYear()}-${_pad2(d.getMonth() + 1)}-${_pad2(d.getDate())}`;
}

const DEFAULT_TRIGGER_CLS =
  "inline-flex w-full items-center justify-between gap-2 rounded-md border border-hairline " +
  "bg-card px-2.5 py-1.5 text-left text-sm text-ink-1 tabular-nums " +
  "focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50";

export const DateTimePicker: React.FC<DateTimePickerProps> = ({
  value,
  onChange,
  withTime = false,
  disabled,
  readOnly,
  invalid,
  placeholder,
  id,
  triggerClassName,
  minDate,
  defaultTime = "09:00",
  timeStep = 30,
  align = "start",
  clearable = true,
  ariaLabel,
  inline = false,
}) => {
  const [open, setOpen] = React.useState(false);
  const locked = disabled || readOnly;
  const slots = React.useMemo(() => buildTimeSlots(timeStep), [timeStep]);
  const activeSlotRef = React.useRef<HTMLButtonElement | null>(null);

  // Con 48 slot l'ora scelta cade quasi sempre fuori dalla finestra visibile.
  React.useEffect(() => {
    if (!withTime || (!inline && !open)) return;
    activeSlotRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [withTime, inline, open]);

  const timeOfDay = React.useMemo(() => {
    if (!value) return defaultTime;
    const hh = String(value.getHours()).padStart(2, "0");
    const mm = String(value.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  }, [value, defaultTime]);

  const handleDayPick = (day: Date | undefined) => {
    if (!day) {
      onChange(null);
      return;
    }
    const merged = new Date(day);
    if (withTime) {
      const [hRaw, mRaw] = timeOfDay.split(":");
      const h = Number.parseInt(hRaw ?? "9", 10);
      const m = Number.parseInt(mRaw ?? "0", 10);
      merged.setHours(Number.isFinite(h) ? h : 9, Number.isFinite(m) ? m : 0, 0, 0);
    } else {
      merged.setHours(0, 0, 0, 0);
    }
    onChange(merged);
    if (!withTime && !inline) setOpen(false);
  };

  const handleTimeChange = (next: string) => {
    if (!value) return;
    const [hRaw, mRaw] = next.split(":");
    const h = Number.parseInt(hRaw ?? "", 10);
    const m = Number.parseInt(mRaw ?? "", 10);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return;
    const merged = new Date(value);
    merged.setHours(h, m, 0, 0);
    onChange(merged);
  };

  // Shared inner panel — Calendar + optional time row + optional clear.
  // Rendered both inside the Popover (default) and standalone (inline).
  const panel = (
    <>
      <Calendar
        mode="single"
        selected={value ?? undefined}
        onSelect={handleDayPick}
        defaultMonth={value ?? undefined}
        disabled={minDate ? { before: startOfDay(minDate) } : undefined}
        autoFocus={!inline}
      />
      {withTime ? (
        <div className="border-hairline border-t px-3 py-2">
          <div className="mb-1.5 font-medium text-[10px] text-ink-3 uppercase tracking-[0.05em]">
            Ora
          </div>
          <fieldset
            // Il nativo ``<input type="time">`` apre la chrome dell'OS: fuori
            // palette e sovrapposta al calendario. Qui la scelta resta dentro
            // la superficie, con la stessa tipografia del resto.
            aria-label={ariaLabel ? `${ariaLabel} — ora` : "Ora"}
            className={cn(
              "grid max-h-[132px] min-w-0 grid-cols-4 gap-1 overflow-y-auto border-0 p-0 pr-1",
              !value && "pointer-events-none opacity-50",
            )}
          >
            {slots.map((slot) => {
              const active = slot === timeOfDay;
              return (
                <button
                  key={slot}
                  ref={active ? activeSlotRef : undefined}
                  type="button"
                  aria-pressed={active}
                  disabled={!value || locked}
                  onClick={() => handleTimeChange(slot)}
                  className={cn(
                    "rounded-md px-1 py-1 text-[11.5px] tabular-nums transition-colors",
                    active
                      ? "bg-ando-orange font-semibold text-white"
                      : "text-ink-2 hover:bg-black/[0.04] hover:text-ink-1",
                  )}
                >
                  {slot}
                </button>
              );
            })}
          </fieldset>
        </div>
      ) : null}
      {clearable && value ? (
        <div className="flex items-center justify-end border-hairline border-t px-3 py-2">
          <button
            type="button"
            className="text-ink-3 text-xs hover:text-ink-1"
            onClick={() => {
              onChange(null);
              if (!inline) setOpen(false);
            }}
          >
            Cancella
          </button>
        </div>
      ) : null}
    </>
  );

  if (inline) {
    return (
      <div className="overflow-hidden rounded-md border border-hairline bg-card">
        {panel}
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={(o) => !locked && setOpen(o)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          disabled={locked}
          aria-invalid={invalid || undefined}
          aria-label={ariaLabel}
          className={cn(
            DEFAULT_TRIGGER_CLS,
            !value && "text-ink-3",
            invalid && "border-destructive focus:ring-destructive",
            triggerClassName,
          )}
        >
          <span className="truncate">
            {value
              ? triggerLabel(value, withTime)
              : (placeholder ?? (withTime ? "Seleziona data e ora" : "Seleziona una data"))}
          </span>
          <CalendarDays className="h-4 w-4 shrink-0 text-ink-3" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align={align} className="w-auto p-0">
        {panel}
      </PopoverContent>
    </Popover>
  );
};
DateTimePicker.displayName = "DateTimePicker";
